import { type FormEvent, useEffect, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { RevealStoredValue } from '@/components/ui/RevealStoredValue';
import { revealSystemDistributorCredentials } from '@/lib/distributors';
import { cn } from '@/lib/cn';
import {
  type ConfigSchemaField,
  type SystemDistributorSettings,
  useSystemDistributorSettings,
  useUpdateSystemDistributorSettings,
} from '@/lib/distributors';

/**
 * Superuser-only panel for managing site-wide distributor credentials.
 * Lives on the admin page next to system settings; mirrors the org-level
 * `DistributorSettings` shape but with two key differences:
 *
 * - There's no "use system credentials" toggle — this IS the system row.
 * - We surface the per-org allowlist counts ("3 organizations have access
 *   revoked"), and clicking the count lets the superuser audit who.
 *
 * Most self-hosted single-user PartForge installs will toggle LCSC on
 * here once and never look at it again. The richer UI is for shared
 * deployments where multiple orgs need oversight.
 */
export function SystemDistributorSettingsPanel() {
  const settings = useSystemDistributorSettings();

  return (
    <section className="rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-5">
      <h2 className="text-lg font-semibold">Site-wide distributor credentials</h2>
      <p className="mt-1 text-sm text-stone-600 dark:text-stone-400">
        Enable distributors for everyone on this PartForge instance, and
        store any credentials they require. Org admins can opt their
        organization in to these site-wide credentials, or supply their
        own. You can revoke access for a specific org at any time.
      </p>

      {settings.isPending && (
        <p className="mt-4 text-sm text-stone-500 dark:text-stone-400">Loading…</p>
      )}

      {settings.isError && (
        <p className="mt-4 text-sm text-red-600">
          {(settings.error as Error).message}
        </p>
      )}

      {settings.data && (
        <ul className="-mx-5 mt-4 divide-y divide-stone-100 dark:divide-stone-800">
          {settings.data.map((row) => (
            <SystemRow key={row.distributor_slug} row={row} />
          ))}
        </ul>
      )}
    </section>
  );
}

function SystemRow({ row }: { row: SystemDistributorSettings }) {
  const [expanded, setExpanded] = useState(false);

  if (!row.adapter_available) {
    return (
      <li className="px-5 py-3 opacity-60">
        <div className="flex items-center gap-2">
          <span className="font-medium">{row.distributor_name}</span>
          <span className="rounded-full bg-stone-100 dark:bg-stone-800 px-2 py-0.5 text-xs text-stone-500 dark:text-stone-400">
            not yet supported
          </span>
        </div>
      </li>
    );
  }

  return (
    <li className="px-5 py-3">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-center gap-2 text-left"
      >
        <span aria-hidden className="text-stone-400 dark:text-stone-500">
          {expanded ? '▾' : '▸'}
        </span>
        <span className="font-medium">{row.distributor_name}</span>
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-xs font-medium',
            row.enabled
              ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
              : 'bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-400',
          )}
        >
          {row.enabled ? 'Enabled' : 'Disabled'}
        </span>
        {row.has_credentials && (
          <span className="text-xs text-stone-500 dark:text-stone-500">
            credentials stored
          </span>
        )}
        {row.blocked_org_slugs.length > 0 && (
          <span className="text-xs text-amber-700 dark:text-amber-400">
            {row.blocked_org_slugs.length} org access revoked
          </span>
        )}
        {row.last_error && (
          <span className="ml-2 truncate text-xs text-red-600" title={row.last_error}>
            ⚠ {row.last_error}
          </span>
        )}
      </button>

      {expanded && <SystemRowEditor row={row} />}
    </li>
  );
}

function SystemRowEditor({ row }: { row: SystemDistributorSettings }) {
  const update = useUpdateSystemDistributorSettings();

  const [enabled, setEnabled] = useState(row.enabled);
  const [credentialsDraft, setCredentialsDraft] = useState<
    Record<string, unknown>
  >({});
  const [credentialsTouched, setCredentialsTouched] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    setEnabled(row.enabled);
    setCredentialsDraft({});
    setCredentialsTouched(false);
  }, [row.distributor_slug]);

  const fields = row.config_schema?.fields ?? [];

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setShowSuccess(false);
    update.mutate(
      {
        distributor_slug: row.distributor_slug,
        enabled,
        credentials: credentialsTouched ? credentialsDraft : null,
      },
      {
        onSuccess: () => {
          setShowSuccess(true);
          setCredentialsTouched(false);
          setCredentialsDraft({});
        },
      },
    );
  };

  const onClearCreds = () => {
    if (!confirm(`Clear stored ${row.distributor_name} credentials?`)) return;
    update.mutate({
      distributor_slug: row.distributor_slug,
      enabled,
      clear_credentials: true,
    });
  };

  return (
    <form
      onSubmit={onSubmit}
      className="mt-3 ml-5 space-y-3 rounded-md bg-stone-50 dark:bg-stone-950/40 p-3"
    >
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        Enable site-wide for {row.distributor_name}
      </label>

      {fields.length > 0 && (
        <div className="space-y-2 rounded border border-stone-200 dark:border-stone-700 p-2">
          <p className="text-xs font-medium text-stone-600 dark:text-stone-400">
            {row.distributor_name} credentials
            {row.has_credentials && (
              <span className="ml-2 text-xs text-stone-500 dark:text-stone-500">
                (already configured — leave blank to keep)
              </span>
            )}
          </p>
          {fields.map((f) => (
            <CredentialField
              key={f.key}
              field={f}
              value={credentialsDraft[f.key]}
              onChange={(value) => {
                setCredentialsDraft((prev) => ({ ...prev, [f.key]: value }));
                setCredentialsTouched(true);
              }}
              revealFetcher={
                f.secret === true && row.has_credentials
                  ? async () => {
                      const creds = await revealSystemDistributorCredentials(
                        row.distributor_slug,
                      );
                      return creds[f.key] ?? null;
                    }
                  : undefined
              }
            />
          ))}
          {row.has_credentials && (
            <button
              type="button"
              onClick={onClearCreds}
              className="text-xs text-red-600 hover:underline"
            >
              Clear stored credentials
            </button>
          )}
        </div>
      )}

      {row.blocked_org_slugs.length > 0 && (
        <div className="rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 p-2 text-xs text-amber-900 dark:text-amber-200">
          <p className="font-medium">Organizations with site-wide access revoked:</p>
          <ul className="mt-1 list-disc list-inside font-mono">
            {row.blocked_org_slugs.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
          <p className="mt-1">
            Use the org-level allowlist endpoint to re-grant access:{' '}
            <code className="font-mono">
              PUT /api/admin/distributor-settings/{row.distributor_slug}/orgs/&lt;slug&gt;/allowlist
            </code>
          </p>
        </div>
      )}

      <div className="flex items-center justify-between">
        <p className="text-xs text-stone-500 dark:text-stone-400">
          {row.daily_request_count > 0
            ? `${row.daily_request_count} requests today`
            : 'No requests yet'}
        </p>
        {update.isError && (
          <p className="text-sm text-red-600">{(update.error as Error).message}</p>
        )}
        {showSuccess && (
          <p className="text-sm text-emerald-700 dark:text-emerald-400">Saved.</p>
        )}
        <Button type="submit" disabled={update.isPending}>
          {update.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  );
}

function CredentialField({
  field,
  value,
  onChange,
  revealFetcher,
}: {
  field: ConfigSchemaField;
  value: unknown;
  onChange: (value: unknown) => void;
  revealFetcher?: () => Promise<string | null>;
}) {
  if (field.type === 'bool') {
    const checked = typeof value === 'boolean' ? value : Boolean(field.default);
    return (
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-1"
        />
        <span>
          <span>{field.label}</span>
          {field.warning && (
            <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-400">
              {field.warning}
            </p>
          )}
        </span>
      </label>
    );
  }
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-xs text-stone-600 dark:text-stone-400">
        {field.label}
      </span>
      <Input
        type={field.secret ? 'password' : 'text'}
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(e.target.value)}
      />
      {revealFetcher && (
        <RevealStoredValue
          fetcher={revealFetcher}
          onReveal={(v) => onChange(v)}
          onHide={() => onChange('')}
          label="stored value"
        />
      )}
      {field.warning && (
        <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
          {field.warning}
        </p>
      )}
    </label>
  );
}
